// The request batch (#2019): screens saved from several Omni pages into one private draft on Lens, the dock that shows
// it on every page from outside the captured portal, and Review request, which turns it into one submitted request.
import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  FEEDBACK_CAPTURE_NOTE_MAX_LENGTH,
  FEEDBACK_MAX_CAPTURES,
  type FeedbackCapture,
  type FeedbackDraft,
  type FeedbackDraftCaptureInput,
  type FeedbackDraftCaptureResponse,
  type FeedbackDraftSubmitInput,
  type FeedbackDraftUpdateInput,
  type FeedbackPage,
  type FeedbackScope,
  type FeedbackSubmitResponse,
} from "@phoneware/lens-domain";
import { ApiError } from "../api.js";
import { errorText, plural, when } from "../format.js";
import { Icon } from "../icons.js";
import { navigate, useLocation } from "../router.js";
import { useLoad, useSession } from "../session.js";
import { Callout, Sheet, useSheetHeld, useToast } from "../ui.js";

/** Where the composer was opened: the header or Requests page for one request, the batch dock for one screen of a batch. */
export type ComposeMode = "single" | "batch";

/** The longest description a request carries, sent at once or reviewed from a batch. */
const DESCRIPTION_MAX = 4000;

/** The page a request or a saved screen came from, as Lens accepts it: the path without query or fragment, and the window. */
export function requestPage(path: string, selectedElement: FeedbackPage["selectedElement"]): FeedbackPage {
  return {
    path: path.startsWith("/") ? path.split(/[?#]/, 1)[0] : "/",
    viewport: {
      width: Math.min(16384, Math.max(1, Math.floor(window.innerWidth))),
      height: Math.min(16384, Math.max(1, Math.floor(window.innerHeight))),
      devicePixelRatio: Math.min(8, Math.max(.1, window.devicePixelRatio || 1)),
    },
    selectedElement,
  };
}

/** The bytes of an image just encoded for Lens, kept in this tab so its thumbnail never downloads them back. */
function imageBlob(screenshot: FeedbackDraftCaptureInput["screenshot"]): Blob {
  const binary = atob(screenshot.dataBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: screenshot.contentType });
}

function notesOf(draft: FeedbackDraft | null): Record<string, string> {
  return Object.fromEntries((draft?.captures ?? []).map((capture) => [capture.id, capture.note ?? ""]));
}

/** Lens refuses a longer note rather than cut it, so the sheet holds it back and says so. */
function tooLong(note: string): boolean {
  return note.trim().length > FEEDBACK_CAPTURE_NOTE_MAX_LENGTH;
}

function listOf(items: readonly (string | number)[]): string {
  return items.length < 2 ? String(items[0] ?? "") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

interface RequestBatch {
  /** The batch as Lens last answered, or null when this person has none. */
  draft: FeedbackDraft | null;
  /** Saves one annotated screen. Its image seeds the Review thumbnail. */
  saveCapture: (input: FeedbackDraftCaptureInput) => Promise<FeedbackDraftCaptureResponse>;
}

const BatchContext = createContext<RequestBatch | null>(null);

export function useRequestBatch(): RequestBatch {
  const batch = useContext(BatchContext);
  if (!batch) throw new Error("The request batch must render inside the Omni shell.");
  return batch;
}

/** What Review request asks of the batch it reviews. Every answer that carries a draft also updates the dock. */
interface BatchOps {
  latest: () => FeedbackDraft | null;
  update: (input: FeedbackDraftUpdateInput) => Promise<FeedbackDraft | null>;
  remove: (captureId: string) => Promise<FeedbackDraft | null>;
  discard: (draftId: string) => Promise<void>;
  submit: (input: FeedbackDraftSubmitInput) => Promise<FeedbackSubmitResponse>;
  reload: () => Promise<FeedbackDraft | null>;
  thumbnail: (captureId: string) => string | null;
  loadThumbnail: (captureId: string) => Promise<string>;
}

/**
 * Holds the signed-in person's batch for the whole shell: read from Lens for whoever may request a change, read again
 * when the person or the act-as session changes, and shown as the dock and, on request, Review request.
 */
export function RequestBatchProvider({ onAddPage, children }: { onAddPage: () => void; children: React.ReactNode }): React.ReactElement {
  const { me, api, can } = useSession();
  const { path } = useLocation();
  const toast = useToast();
  const enabled = can("requests.create");
  // One batch per person, per dealer and per act-as session, as Lens keys drafts (spec 3.4).
  const owner = `${me.user.email}\u0000${me.user.dealerId ?? "-"}\u0000${me.actingAs?.sessionId ?? "-"}`;
  const [draft, setDraft] = useState<FeedbackDraft | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const latest = useRef<FeedbackDraft | null>(null);
  const ownerNow = useRef(owner);
  // Every read takes the next number, and so does every answered change; a read whose number is no longer current is dropped.
  const generation = useRef(0);
  const unread = useRef(false);
  // Drafts Lens has deleted: a late answer naming one never brings it back.
  const gone = useRef(new Set<string>());
  const thumbs = useRef(new Map<string, string>());
  const pending = useRef(new Map<string, Promise<string>>());

  const apply = useCallback((next: FeedbackDraft | null): void => {
    const now = latest.current;
    if (next && gone.current.has(next.id)) return;
    if (next && now && next.id === now.id && next.revision < now.revision) return;
    if (now && (!next || next.id !== now.id)) gone.current.add(now.id);
    latest.current = next;
    setDraft(next);
    const keep = new Set(next?.captures.map((capture) => capture.id) ?? []);
    for (const [id, url] of thumbs.current) {
      if (keep.has(id)) continue;
      URL.revokeObjectURL(url);
      thumbs.current.delete(id);
    }
  }, []);

  const read = useCallback(async (): Promise<FeedbackDraft | null> => {
    const turn = ++generation.current;
    try {
      const answer = await api.feedbackDraft();
      if (turn === generation.current) {
        unread.current = false;
        apply(answer.draft);
      }
    } catch (error) {
      if (turn === generation.current) unread.current = true;
      throw error;
    }
    return latest.current;
  }, [api, apply]);

  const mutate = useCallback(async <T,>(call: () => Promise<T>, draftOf: (value: T) => FeedbackDraft | null): Promise<T> => {
    const at = ownerNow.current;
    const value = await call();
    if (ownerNow.current === at) {
      // An answered change is newer than any read already on its way.
      generation.current++;
      unread.current = false;
      apply(draftOf(value));
    }
    return value;
  }, [apply]);

  useEffect(() => {
    ownerNow.current = owner;
    generation.current++;
    latest.current = null;
    gone.current.clear();
    pending.current.clear();
    for (const url of thumbs.current.values()) URL.revokeObjectURL(url);
    thumbs.current.clear();
    setDraft(null);
    setReviewing(false);
    if (enabled) read().catch(() => undefined);
  }, [enabled, owner, read]);

  // A failed read keeps the dock away; moving to another page asks again.
  useEffect(() => {
    if (enabled && unread.current) read().catch(() => undefined);
  }, [path]);

  useEffect(() => () => {
    for (const url of thumbs.current.values()) URL.revokeObjectURL(url);
    thumbs.current.clear();
  }, []);

  const saveCapture = useCallback(async (input: FeedbackDraftCaptureInput): Promise<FeedbackDraftCaptureResponse> => {
    const answer = await mutate(() => api.saveFeedbackDraftCapture(input), (value) => value.draft);
    const id = answer.capture.id;
    if (!thumbs.current.has(id) && latest.current?.captures.some((capture) => capture.id === id)) {
      thumbs.current.set(id, URL.createObjectURL(imageBlob(input.screenshot)));
    }
    return answer;
  }, [api, mutate]);

  const ops = useMemo<BatchOps>(() => ({
    latest: () => latest.current,
    update: (input) => mutate(() => api.updateFeedbackDraft(input), (value) => value.draft).then((value) => value.draft),
    remove: (captureId) => mutate(() => api.removeFeedbackDraftCapture(captureId), (value) => value.draft).then((value) => value.draft),
    discard: (draftId) => mutate(() => api.discardFeedbackDraft({ draftId }), (value) => value.draft).then(() => undefined),
    submit: (input) => mutate(() => api.submitFeedbackDraft(input), () => null),
    reload: read,
    thumbnail: (captureId) => thumbs.current.get(captureId) ?? null,
    loadThumbnail: (captureId) => {
      const cached = thumbs.current.get(captureId);
      if (cached) return Promise.resolve(cached);
      const waiting = pending.current.get(captureId);
      if (waiting) return waiting;
      const at = ownerNow.current;
      const load = api.feedbackDraftCapture(captureId).then(
        (blob) => {
          if (pending.current.get(captureId) === load) pending.current.delete(captureId);
          const ready = thumbs.current.get(captureId);
          if (ready) return ready;
          if (ownerNow.current !== at || !latest.current?.captures.some((capture) => capture.id === captureId)) throw new Error("Screen not found.");
          const url = URL.createObjectURL(blob);
          thumbs.current.set(captureId, url);
          return url;
        },
        (error: unknown) => {
          if (pending.current.get(captureId) === load) pending.current.delete(captureId);
          throw error;
        },
      );
      pending.current.set(captureId, load);
      return load;
    },
  }), [api, mutate, read]);

  const value = useMemo<RequestBatch>(() => ({ draft, saveCapture }), [draft, saveCapture]);
  return (
    <BatchContext.Provider value={value}>
      {children}
      {draft ? <BatchDock draft={draft} onAdd={onAddPage} onReview={() => setReviewing(true)} /> : null}
      {reviewing ? (
        <BatchReview
          draft={draft}
          ops={ops}
          onClose={() => setReviewing(false)}
          onSubmitted={(id) => { setReviewing(false); navigate(`/requests/${encodeURIComponent(id)}`); }}
          onDiscarded={() => { setReviewing(false); toast("Batch discarded."); }}
          onEmptied={() => { setReviewing(false); toast("Batch removed. It had no screens or description."); }}
        />
      ) : null}
    </BatchContext.Provider>
  );
}

/**
 * The batch on every page, rendered on the body outside .app so a capture never includes it. It hides and goes inert
 * while any Sheet is open, and its ten cells are Omni's segmented progress bar stretched to the ten-screen limit.
 */
function BatchDock({ draft, onAdd, onReview }: { draft: FeedbackDraft; onAdd: () => void; onReview: () => void }): React.ReactElement {
  const held = useSheetHeld();
  const dock = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    if (dock.current) dock.current.inert = held;
  }, [held]);
  const count = draft.captures.length;
  const expired = draft.captures.filter((capture) => !capture.available).length;
  return createPortal(
    <section ref={dock} className="request-dock" role="region" aria-label="Request batch" data-testid="request-batch-dock" hidden={held}>
      <div className="request-dock-in">
        <span className="request-dock-ic"><Icon name="layers" /></span>
        <div className="request-dock-text">
          <b>Request batch</b>
          <span>{plural(count, "screen")} saved{expired ? `, ${expired} expired` : ""}</span>
        </div>
        <span className="request-dock-meter" role="img" aria-label={`${count} of ${FEEDBACK_MAX_CAPTURES} screens`}>
          {Array.from({ length: FEEDBACK_MAX_CAPTURES }, (_, i) => (
            <i key={i} className={i < count ? (draft.captures[i].available ? "on" : "exp") : undefined} />
          ))}
        </span>
        <div className="request-dock-actions">
          <button className="btn" type="button" aria-haspopup="dialog" data-testid="request-batch-add" disabled={count >= FEEDBACK_MAX_CAPTURES} onClick={onAdd}>
            <Icon name="scan-text" />Add this page
          </button>
          <button className="btn primary" type="button" aria-haspopup="dialog" data-testid="request-batch-review" onClick={onReview}>
            Review and submit
          </button>
        </div>
      </div>
    </section>,
    document.body,
  );
}

/** A saved screen's thumbnail: from this tab's cache, or downloaded once it scrolls into view. */
function Thumb({ capture, ops }: { capture: FeedbackCapture; ops: BatchOps }): React.ReactElement {
  const box = useRef<HTMLDivElement>(null);
  const [url, setUrl] = useState<string | null>(() => ops.thumbnail(capture.id));
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (url || failed || !capture.available) return;
    const node = box.current;
    if (!node) return;
    let alive = true;
    const load = (): void => {
      ops.loadThumbnail(capture.id).then(
        (next) => { if (alive) setUrl(next); },
        () => { if (alive) setFailed(true); },
      );
    };
    if (typeof IntersectionObserver !== "function") {
      load();
      return () => { alive = false; };
    }
    const watch = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      watch.disconnect();
      load();
    }, { rootMargin: "160px" });
    watch.observe(node);
    return () => {
      alive = false;
      watch.disconnect();
    };
  }, [capture.id, capture.available, url, failed, ops]);
  if (!capture.available) {
    return <div className="request-review-thumb expired"><Icon name="image-off" size={18} /><span>Expired</span></div>;
  }
  return <div ref={box} className="request-review-thumb">{url ? <img src={url} alt="" /> : failed ? <span>Preview unavailable</span> : null}</div>;
}

type SaveState = "idle" | "saving" | "saved" | "failed";
type Action = "" | "submit" | "remove" | "discard";

/**
 * Review request: one description, the screens in the order they were saved with a note each, and Submit. Text saves
 * 800 ms after the last keystroke and on blur, one PATCH at a time, and what the person is typing is never replaced by
 * an answer from Lens.
 */
function BatchReview({ draft, ops, onClose, onSubmitted, onDiscarded, onEmptied }: {
  draft: FeedbackDraft | null;
  ops: BatchOps;
  onClose: () => void;
  onSubmitted: (requestId: string) => void;
  onDiscarded: () => void;
  onEmptied: () => void;
}): React.ReactElement {
  const { me, api } = useSession();
  const toast = useToast();
  const listing = useLoad(() => api.feedbackRequests({ limit: 1 }), [api]);
  const choices = listing.data?.capabilities.chooseScope ?? false;
  const dealers = useLoad(() => (choices ? api.dealers() : Promise.resolve([])), [api, choices]);
  const [scopeValue, setScopeValue] = useState("");
  const [comment, setComment] = useState(draft?.comment ?? "");
  const [notes, setNotes] = useState<Record<string, string>>(() => notesOf(draft));
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [problem, setProblem] = useState("");
  const [action, setAction] = useState<Action>("");
  const [confirming, setConfirming] = useState(false);
  // What the person has typed, read by saves that outlive the render that started them.
  const typed = useRef({ comment: draft?.comment ?? "", notes: notesOf(draft) });
  // What Lens last accepted from this sheet, or holds after a reload: the baseline the next save compares against.
  const saved = useRef({ comment: draft?.comment ?? "", notes: notesOf(draft) });
  const timer = useRef<number | undefined>(undefined);
  const inFlight = useRef<Promise<boolean> | null>(null);
  const again = useRef(false);
  const lastError = useRef("");
  const submission = useRef<{ key: string; id: string } | null>(null);
  const description = useRef<HTMLTextAreaElement>(null);
  const list = useRef<HTMLOListElement>(null);
  const keep = useRef<HTMLButtonElement>(null);
  const discardButton = useRef<HTMLButtonElement>(null);
  const asked = useRef(false);
  const busy = action !== "";

  useEffect(() => () => window.clearTimeout(timer.current), []);
  // A screen that left the batch takes its note with it; one that arrived with a reload brings the note Lens holds.
  useEffect(() => {
    if (!draft) return;
    const next: Record<string, string> = {};
    const baseline: Record<string, string> = {};
    for (const capture of draft.captures) {
      next[capture.id] = capture.id in typed.current.notes ? typed.current.notes[capture.id] : capture.note ?? "";
      baseline[capture.id] = capture.id in saved.current.notes ? saved.current.notes[capture.id] : capture.note ?? "";
    }
    typed.current = { ...typed.current, notes: next };
    saved.current = { ...saved.current, notes: baseline };
    setNotes(next);
  }, [draft]);
  useEffect(() => {
    if (confirming) {
      asked.current = true;
      keep.current?.focus();
    } else if (asked.current) {
      asked.current = false;
      discardButton.current?.focus();
    }
  }, [confirming]);

  /** The changes Lens has not accepted yet, as one PATCH: the description and every changed note within its limit. */
  const changes = (): { input: FeedbackDraftUpdateInput; comment: string | undefined; notes: Record<string, string> } | null => {
    const current = ops.latest();
    if (!current) return null;
    const input: FeedbackDraftUpdateInput = { draftId: current.id };
    const text = typed.current.comment;
    const sentComment = text !== saved.current.comment && text.length <= DESCRIPTION_MAX ? text : undefined;
    if (sentComment !== undefined) input.comment = sentComment;
    const changed = current.captures.filter((capture) => {
      const note = typed.current.notes[capture.id];
      return note !== undefined && note !== saved.current.notes[capture.id] && !tooLong(note);
    });
    const sentNotes: Record<string, string> = {};
    if (changed.length) {
      input.notes = changed.map((capture) => ({ captureId: capture.id, note: typed.current.notes[capture.id].trim() || null }));
      for (const capture of changed) sentNotes[capture.id] = typed.current.notes[capture.id];
    }
    return sentComment === undefined && !changed.length ? null : { input, comment: sentComment, notes: sentNotes };
  };

  /** After a 409: reads the batch again and keeps every word typed. What Lens holds becomes the baseline for the next save. */
  const reloadKeepingText = async (): Promise<void> => {
    try {
      const fresh = await ops.reload();
      saved.current = { comment: fresh?.comment ?? "", notes: notesOf(fresh) };
    } catch (error) {
      setProblem(errorText(error));
    }
  };

  /** Saves what is typed now, one PATCH at a time, and answers whether everything typed is saved. */
  const flush = (): Promise<boolean> => {
    window.clearTimeout(timer.current);
    if (inFlight.current) {
      again.current = true;
      return inFlight.current;
    }
    const change = changes();
    if (!change) {
      setSaveState((state) => (state === "saving" ? "saved" : state));
      return Promise.resolve(true);
    }
    const run = (async (): Promise<boolean> => {
      setSaveState("saving");
      let ok = true;
      try {
        await ops.update(change.input);
        saved.current = {
          comment: change.comment ?? saved.current.comment,
          notes: { ...saved.current.notes, ...change.notes },
        };
        lastError.current = "";
        setSaveState("saved");
        setProblem("");
      } catch (error) {
        ok = false;
        lastError.current = errorText(error);
        setSaveState("failed");
        setProblem(lastError.current);
        if (error instanceof ApiError && error.status === 409) await reloadKeepingText();
      } finally {
        inFlight.current = null;
      }
      if (again.current) {
        again.current = false;
        return (await flush()) && ok;
      }
      return ok;
    })();
    inFlight.current = run;
    return run;
  };

  const schedule = (): void => {
    window.clearTimeout(timer.current);
    setSaveState("saving");
    timer.current = window.setTimeout(() => { void flush(); }, 800);
  };
  const typeComment = (value: string): void => {
    typed.current = { ...typed.current, comment: value };
    setComment(value);
    schedule();
  };
  const typeNote = (captureId: string, value: string): void => {
    typed.current = { ...typed.current, notes: { ...typed.current.notes, [captureId]: value } };
    setNotes(typed.current.notes);
    schedule();
  };

  const scope: FeedbackScope | undefined = !choices ? undefined : scopeValue === "platform" ? { kind: "platform" } : scopeValue.startsWith("dealer:") ? { kind: "dealer", dealerId: scopeValue.slice(7) } : undefined;
  const ready = !!draft && !!listing.data?.capabilities.create && (!choices || !!scope) && (scope?.kind !== "dealer" || !!dealers.data?.some((dealer) => dealer.id === scope.dealerId));
  const captures = draft?.captures ?? [];
  const expired = captures.map((capture, position) => (capture.available ? 0 : position + 1)).filter((k) => k > 0);
  const longNote = captures.some((capture) => tooLong(notes[capture.id] ?? ""));

  const close = (): void => {
    if (busy) return;
    void flush().then((ok) => {
      if (!ok) toast(`Your last edits to the batch were not saved. ${lastError.current}`);
    });
    onClose();
  };

  const submit = async (): Promise<void> => {
    if (busy || !ready) return;
    if (!typed.current.comment.trim()) {
      setProblem("Describe the change before submitting.");
      description.current?.focus();
      return;
    }
    setAction("submit");
    setProblem("");
    try {
      // Pending saves land first, so the request carries what is on screen and the revision Lens gave it.
      if (!(await flush())) return;
      const current = ops.latest();
      if (!current) return;
      const key = `${current.id}:${current.revision}`;
      if (submission.current?.key !== key) submission.current = { key, id: crypto.randomUUID() };
      const answer = await ops.submit({
        clientSubmissionId: submission.current.id,
        draftId: current.id,
        expectedRevision: current.revision,
        page: requestPage(window.location.pathname, null),
        ...(scope ? { scope } : {}),
      });
      onSubmitted(answer.request.id);
    } catch (error) {
      setProblem(errorText(error));
      if (error instanceof ApiError && error.status === 409) await reloadKeepingText();
    } finally {
      setAction("");
    }
  };

  const remove = async (capture: FeedbackCapture, position: number): Promise<void> => {
    if (busy) return;
    setAction("remove");
    setProblem("");
    try {
      // The typed description lands first, so taking out the last screen never takes a described batch with it.
      if (!(await flush())) return;
      const next = await ops.remove(capture.id);
      if (!next) {
        onEmptied();
        return;
      }
      requestAnimationFrame(() => {
        const buttons = list.current?.querySelectorAll<HTMLButtonElement>(".request-review-remove");
        const target: HTMLElement | null | undefined = buttons?.length ? buttons[Math.min(position, buttons.length - 1)] : description.current;
        target?.focus();
      });
    } catch (error) {
      setProblem(errorText(error));
      if (error instanceof ApiError && (error.status === 404 || error.status === 409)) await reloadKeepingText();
    } finally {
      setAction("");
    }
  };

  const discard = async (): Promise<void> => {
    const current = ops.latest();
    if (busy || !current) return;
    setAction("discard");
    setProblem("");
    window.clearTimeout(timer.current);
    try {
      // A save already on its way lands first, so its answer can't bring the batch back after it is gone.
      await inFlight.current;
      await ops.discard(current.id);
      onDiscarded();
    } catch (error) {
      setConfirming(false);
      setProblem(errorText(error));
      if (error instanceof ApiError && error.status === 409) await reloadKeepingText();
    } finally {
      setAction("");
    }
  };

  const count = captures.length;
  const status = saveState === "saving" ? "Saving changes" : longNote || saveState === "failed" ? "Not saved" : saveState === "saved" ? "All changes saved" : "Changes save as you type";
  const losing = [count ? plural(count, "screen") : "", comment.trim() ? "the description" : ""].filter(Boolean);
  const foot = !draft ? (
    <button className="btn" type="button" onClick={onClose}>Close</button>
  ) : confirming ? (
    <div className="request-review-foot">
      <p className="request-review-confirm">{`Discard ${losing.length ? listOf(losing) : "this batch"}? This can\u2019t be undone.`}</p>
      <button ref={keep} className="btn" type="button" disabled={busy} onClick={() => setConfirming(false)}>Keep it</button>
      <button className="btn stopfill" type="button" disabled={busy} data-testid="request-batch-discard-confirm" onClick={() => { void discard(); }}>
        <Icon name="trash-2" size={15} />Discard batch
      </button>
    </div>
  ) : (
    <div className="request-review-foot">
      <button ref={discardButton} className="btn danger" type="button" disabled={busy} onClick={() => setConfirming(true)}>
        <Icon name="trash-2" size={15} />Discard batch
      </button>
      <span className="request-review-status">{status}</span>
      <button className="btn primary" type="button" disabled={!ready || busy || expired.length > 0 || longNote} data-testid="request-batch-submit" onClick={() => { void submit(); }}>
        {action === "submit" ? "Submitting request" : "Submit request"}
      </button>
    </div>
  );

  return (
    <Sheet title="Review request" wide onClose={close} foot={foot}>
      <div className="request-review">
        <label className="field"><span>Describe the change</span>
          <textarea ref={description} className="input" value={comment} maxLength={DESCRIPTION_MAX} placeholder="What should change in Omni?" readOnly={!draft}
            onChange={(event) => typeComment(event.target.value)} onBlur={() => { void flush(); }} />
        </label>
        {problem ? <p role="alert" className="form-error">{problem}</p> : null}
        {!draft ? <Callout kind="stop" icon="triangle-alert">This batch was submitted or discarded, so it can no longer change. What you typed is still here to copy.</Callout> : null}
        {listing.error || dealers.error ? <p role="alert" className="form-error">{listing.error?.message ?? dealers.error?.message}</p> : null}
        {choices ? (
          <>
            <label className="field"><span>Scope</span>
              <select className="input" aria-label="Scope" value={scopeValue} onChange={(event) => setScopeValue(event.target.value)}>
                <option value="">Scope</option><option value="platform">Platform</option>
                {(dealers.data ?? []).map((dealer) => <option key={dealer.id} value={`dealer:${dealer.id}`}>{dealer.name}</option>)}
              </select>
            </label>
            {scope?.kind === "dealer" ? <p className="scope-note">{"Everyone who can see this dealer's requests will see every screen."}</p> : null}
          </>
        ) : me.dealer ? <p className="scope-note">Scope: {me.dealer.name}</p> : null}
        {draft ? (
          <>
            <div className="request-review-h"><span className="label">Screens</span><span>{count} of {FEEDBACK_MAX_CAPTURES}</span></div>
            {expired.length ? (
              <Callout kind="warn" icon="triangle-alert">
                {expired.length === 1 ? `Screen ${expired[0]} has expired. Remove it to submit the batch.` : `Screens ${listOf(expired)} have expired. Remove them to submit the batch.`}
              </Callout>
            ) : null}
            {count ? (
              <ol ref={list} className="request-review-screens">
                {captures.map((capture, position) => {
                  const k = position + 1;
                  const note = notes[capture.id] ?? "";
                  return (
                    <li key={capture.id} className="request-review-screen" data-testid={`request-batch-screen-${k}`}>
                      <Thumb capture={capture} ops={ops} />
                      <div className="request-review-head">
                        <div>
                          <b>Screen {k}</b>
                          <span className="request-review-meta">
                            Page {capture.page.path}
                            {capture.page.selectedElement ? ` \u00b7 ${capture.page.selectedElement.tagName} selected` : ""}
                            {` \u00b7 ${capture.available ? "captured" : "expired, captured"} ${when(capture.capturedAt)}`}
                          </span>
                        </div>
                        <button className="btn small danger request-review-remove" type="button" disabled={busy} onClick={() => { void remove(capture, position); }}>
                          <Icon name="trash-2" size={14} />Remove<span className="sr"> screen {k}</span>
                        </button>
                      </div>
                      <div className="request-review-note">
                        <label className="field"><span>Note for screen {k}</span>
                          <textarea className="input" value={note} placeholder="What should change on this screen?"
                            onChange={(event) => typeNote(capture.id, event.target.value)} onBlur={() => { void flush(); }} />
                        </label>
                        {tooLong(note) ? <p className="form-error">Shorten this note to {FEEDBACK_CAPTURE_NOTE_MAX_LENGTH.toLocaleString("en-US")} characters to save it.</p> : null}
                      </div>
                    </li>
                  );
                })}
              </ol>
            ) : (
              <div className="request-review-empty">
                <Icon name="layers" />
                <div><b>No screens in this batch</b>Submit the description on its own, or close this and use Add this page on any Omni page.</div>
              </div>
            )}
          </>
        ) : null}
      </div>
    </Sheet>
  );
}
